/**
 * FR5 — Fullscreen scene mode (mock 13): big looping player of the active
 * take, TakesRail with pick-the-best, scene setup, mini-timeline navigation.
 */

import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import type { AdCreativeItem, AdCreativeVideoPlan, AdCreativeVideoSegmentTake } from '@segmently/schema-models/ad-creative';
import type { MediaLightboxItem } from '@modules/ui-components/dialogs/MediaLightbox';
import { AD_CREATIVE_STORYBOARD } from '../../selectors';
import { INSIGHT_CATEGORY_COLOR } from '../../services/AdCreativeInsightContextClient';
import { StyledTextField } from '@modules/ui-components';
import { segmentThumbUrl, VIDEO_USD_PER_SECOND, type ThesisOption } from './StoryboardWorkspace';

const SB = AD_CREATIVE_STORYBOARD;

interface SceneFullscreenProps {
  item: AdCreativeItem;
  plan: AdCreativeVideoPlan;
  sceneIndex: number;
  running: boolean;
  busy: string | null;
  error: string | null;
  thesisOptions: ThesisOption[];
  onBack: (currentIndex: number) => void;
  onNavigate: (index: number) => void;
  onUseTake: (index: number, takeId: string) => Promise<boolean>;
  onDeleteTake: (index: number, takeId: string) => Promise<boolean>;
  onReroll: (index: number) => Promise<boolean>;
  onRenderWithPatch: (index: number, patch: { motionPrompt?: string; keyframePrompt?: string }) => Promise<boolean>;
  onSetThesis: (index: number, sourcePath: string | null) => Promise<boolean>;
  onRetime: (index: number, durationSec: number) => Promise<boolean>;
  onOpenLightbox: (items: MediaLightboxItem[]) => void;
}

export function SceneFullscreen({
  item,
  plan,
  sceneIndex,
  running,
  busy,
  error,
  thesisOptions,
  onBack,
  onNavigate,
  onUseTake,
  onDeleteTake,
  onReroll,
  onRenderWithPatch,
  onSetThesis,
  onRetime,
  onOpenLightbox,
}: SceneFullscreenProps): React.ReactElement {
  const segments = [...plan.segments].sort((a, b) => a.index - b.index);
  const scene = segments.find((entry) => entry.index === sceneIndex) ?? segments[0];
  const [editing, setEditing] = useState(false);
  const [motionDraft, setMotionDraft] = useState(scene.motionPrompt);
  const [keyframeDraft, setKeyframeDraft] = useState(scene.keyframePrompt);
  const [thesisAnchor, setThesisAnchor] = useState<HTMLElement | null>(null);
  const [durationAnchor, setDurationAnchor] = useState<HTMLElement | null>(null);
  const [takeMenu, setTakeMenu] = useState<{ anchor: HTMLElement; takeId: string } | null>(null);

  const takes = scene.takes ?? [];
  const activeTake = takes.find((take) => take.id === scene.activeTakeId);
  const isChain = scene.continuityMode === 'chain';
  // FR6-F3: no explicit override → fall back to the brief's role mapping (auto).
  const thesisOption = scene.sourcePath
    ? thesisOptions.find((option) => option.sourcePath === scene.sourcePath)
    : undefined;
  const briefMapping = !scene.sourcePath
    ? (item.videoBrief?.insightMapping ?? []).find((candidate) => candidate.role === scene.role)
    : undefined;
  const thesisLabel = thesisOption
    ? `${thesisOption.category} · ${thesisOption.label.slice(0, 40)}…`
    : briefMapping?.label || briefMapping?.sourcePath
      ? `${(briefMapping!.label ?? briefMapping!.sourcePath ?? '').slice(0, 48)} · auto`
      : scene.role === 'cta' ? 'no source needed' : 'no thesis';
  const sceneCost = (scene.durationSec * VIDEO_USD_PER_SECOND).toFixed(2);
  const position = segments.findIndex((entry) => entry.index === scene.index);
  const previous = segments[position - 1];
  const next = segments[position + 1];

  const takeLabel = (take: AdCreativeVideoSegmentTake, index: number): string =>
    take.label ?? (index === 0 ? 'first render' : `re-roll ${index}`);

  return (
    <Box data-testid={SB.fullscreen} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button size="small" data-testid={SB.fsBack} onClick={() => onBack(scene.index)}>⤢ Back to timeline</Button>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }} noWrap>
          Scene {scene.index + 1} · {(scene.role ?? 'scene').toUpperCase()} — fullscreen
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Button size="small" data-testid={SB.fsNav('prev')} disabled={!previous} onClick={() => previous && onNavigate(previous.index)}>
            ‹ Scene {previous ? previous.index + 1 : '—'}
          </Button>
          <Typography variant="caption" color="text.secondary">{position + 1} of {segments.length}</Typography>
          <Button size="small" data-testid={SB.fsNav('next')} disabled={!next} onClick={() => next && onNavigate(next.index)}>
            Scene {next ? next.index + 1 : '—'} ›
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        {/* Big player: the ACTIVE take, looping */}
        <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
          <Box
            data-testid={SB.fsPlayer}
            sx={{ aspectRatio: '9/16', borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.900', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            {activeTake?.clipUrl ? (
              <video key={activeTake.clipUrl} src={activeTake.clipUrl} controls loop autoPlay muted style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <Typography variant="caption" sx={{ color: 'grey.500', px: 2, textAlign: 'center' }}>
                No rendered take yet — render this scene below.
              </Typography>
            )}
            {activeTake?.clipUrl && (
              <IconButton
                size="small"
                onClick={() => onOpenLightbox([{ id: `take-${activeTake.id}`, kind: 'video', src: activeTake.clipUrl!, title: `Scene ${scene.index + 1} · active take` }])}
                sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,.55)', color: '#fff' }}
              >
                🔍
              </IconButton>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            ⟳ loop · take {activeTake ? `#${takes.findIndex((take) => take.id === activeTake.id) + 1}` : '—'} · {scene.durationSec}s
          </Typography>
        </Box>

        <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {/* Takes rail */}
          <Box data-testid={SB.takesRail} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Takes — variants of this scene
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>kept until you delete them</Typography>
            </Typography>
            <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
              {takes.map((take, index) => {
                const inUse = take.id === scene.activeTakeId;
                return (
                  <Box
                    key={take.id}
                    data-testid={SB.takeCard(index)}
                    sx={{
                      width: 132, flexShrink: 0, border: 2, borderRadius: 1, p: 0.75,
                      borderColor: inUse ? 'primary.main' : 'divider',
                    }}
                  >
                    <Box sx={{ aspectRatio: '9/16', borderRadius: 0.5, bgcolor: 'grey.800', position: 'relative', overflow: 'hidden', mb: 0.5 }}>
                      {take.clipUrl && (
                        <video src={take.clipUrl} muted preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      )}
                      {inUse && (
                        <Chip size="small" color="primary" data-testid={SB.takeInUse} label="in use" sx={{ position: 'absolute', top: 4, left: 4, height: 18, fontSize: 10 }} />
                      )}
                      <IconButton
                        size="small"
                        data-testid={SB.takeMenu(index)}
                        onClick={(event) => setTakeMenu({ anchor: event.currentTarget, takeId: take.id })}
                        sx={{ position: 'absolute', top: 2, right: 2, color: '#fff', bgcolor: 'rgba(0,0,0,.4)', width: 22, height: 22, fontSize: 12 }}
                      >
                        ⋮
                      </IconButton>
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }} noWrap>
                      Take {index + 1}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {takeLabel(take, index)}
                    </Typography>
                    <Button
                      size="small"
                      fullWidth
                      variant={inUse ? 'text' : 'outlined'}
                      data-testid={SB.takeUse(index)}
                      disabled={inUse || busy !== null || take.status !== 'completed'}
                      onClick={() => void onUseTake(scene.index, take.id)}
                      sx={{ mt: 0.5 }}
                    >
                      {inUse ? '✓ In the full cut' : 'Use this take · free'}
                    </Button>
                  </Box>
                );
              })}
              {/* + New take */}
              <Stack
                spacing={0.5}
                sx={{ width: 132, flexShrink: 0, border: '2px dashed', borderColor: 'divider', borderRadius: 1, p: 1, justifyContent: 'center' }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600 }}>+ New take</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  data-testid={SB.newTakeReroll}
                  disabled={running || busy !== null}
                  onClick={() => void onReroll(scene.index)}
                >
                  Re-roll same prompt · ≈${sceneCost}
                </Button>
                <Button
                  size="small"
                  data-testid={SB.newTakeEditFirst}
                  disabled={running || busy !== null}
                  onClick={() => {
                    setEditing(true);
                    setMotionDraft(scene.motionPrompt);
                    setKeyframeDraft(scene.keyframePrompt);
                  }}
                >
                  Edit prompt first
                </Button>
              </Stack>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              The chosen take is what gets stitched into the full cut. Switching is free (re-stitch only).
            </Typography>
          </Box>

          {/* Scene setup */}
          <Box data-testid={SB.sceneSetup} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Scene setup</Typography>
            {editing ? (
              <Stack spacing={1}>
                {!isChain && (
                  <StyledTextField
                    label="Keyframe prompt (first frame)"
                    value={keyframeDraft}
                    onChange={setKeyframeDraft}
                    multiline
                    minRows={3}
                    size="small"
                    fullWidth
                  />
                )}
                <StyledTextField
                  label="Motion prompt"
                  value={motionDraft}
                  onChange={setMotionDraft}
                  multiline
                  minRows={3}
                  size="small"
                  fullWidth
                />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={running || busy !== null}
                    onClick={() => {
                      void onRenderWithPatch(scene.index, {
                        motionPrompt: motionDraft,
                        ...(isChain || keyframeDraft === scene.keyframePrompt ? {} : { keyframePrompt: keyframeDraft }),
                      }).then((ok) => {
                        if (ok) setEditing(false);
                      });
                    }}
                  >
                    Render as new take · ≈${sceneCost} · estimated
                  </Button>
                  <Button size="small" onClick={() => setEditing(false)}>Cancel</Button>
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    edits never overwrite existing takes
                  </Typography>
                </Stack>
              </Stack>
            ) : (
              <Stack spacing={0.75}>
                {!isChain && (
                  <Typography variant="body2" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    <b>Keyframe:</b> {scene.keyframePrompt}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  <b>Motion:</b> {scene.motionPrompt}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={thesisOption ? INSIGHT_CATEGORY_COLOR[thesisOption.category] ?? 'default' : 'default'}
                    label={thesisLabel}
                  />
                  <Button size="small" disabled={running} onClick={(event) => setThesisAnchor(event.currentTarget)}>change ▾</Button>
                  <Button size="small" disabled={running} onClick={(event) => setDurationAnchor(event.currentTarget)} sx={{ ml: 'auto' }}>
                    Duration: {scene.durationSec}s ▾
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    continuity: {isChain ? 'chain' : 'own keyframe'}
                  </Typography>
                </Stack>
              </Stack>
            )}
          </Box>

          {/* Mini timeline */}
          <Box data-testid={SB.fsMiniTimeline} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Timeline · click to jump
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ height: 44 }}>
              {segments.map((segment) => {
                const thumb = segmentThumbUrl(item, segment);
                const isCurrent = segment.index === scene.index;
                return (
                  <Box
                    key={segment.index}
                    data-testid={SB.fsMiniSegment(segment.index)}
                    onClick={() => onNavigate(segment.index)}
                    sx={{
                      flex: segment.durationSec, minWidth: 34, borderRadius: 0.5, position: 'relative', cursor: 'pointer',
                      overflow: 'hidden', bgcolor: 'grey.800', border: 2,
                      borderColor: isCurrent ? 'primary.main' : segment.status === 'completed' ? 'success.main' : segment.status === 'error' ? 'error.main' : 'divider',
                    }}
                  >
                    {thumb && <Box component="img" src={thumb} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                    <Typography variant="caption" sx={{ position: 'absolute', top: 1, left: 3, color: '#fff', textShadow: '0 0 3px #000', fontSize: 9 }}>
                      {segment.index + 1}{segment.status === 'completed' ? ' ✓' : ''}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Stack>
      </Stack>

      {/* Take overflow menu (deleteTake, minimal affordance) */}
      <Menu open={Boolean(takeMenu)} anchorEl={takeMenu?.anchor} onClose={() => setTakeMenu(null)}>
        <MenuItem
          disabled={takeMenu?.takeId === scene.activeTakeId}
          onClick={() => {
            const target = takeMenu?.takeId;
            setTakeMenu(null);
            if (target) void onDeleteTake(scene.index, target);
          }}
        >
          <ListItemText primary="Delete take" secondary={takeMenu?.takeId === scene.activeTakeId ? 'switch takes first' : undefined} />
        </MenuItem>
      </Menu>

      {/* Thesis menu (selection only) */}
      <Menu open={Boolean(thesisAnchor)} anchorEl={thesisAnchor} onClose={() => setThesisAnchor(null)}>
        <MenuItem onClick={() => { setThesisAnchor(null); void onSetThesis(scene.index, null); }}>
          <ListItemText primary="No source (brand-generic)" />
        </MenuItem>
        {thesisOptions.map((option) => (
          <MenuItem
            key={option.sourcePath}
            onClick={() => { setThesisAnchor(null); void onSetThesis(scene.index, option.sourcePath); }}
          >
            <Chip size="small" color={INSIGHT_CATEGORY_COLOR[option.category] ?? 'default'} label={option.category} sx={{ mr: 1 }} />
            <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={option.label.slice(0, 80)} />
          </MenuItem>
        ))}
      </Menu>

      {/* Duration menu */}
      <Menu open={Boolean(durationAnchor)} anchorEl={durationAnchor} onClose={() => setDurationAnchor(null)}>
        {[4, 6, 8].map((seconds) => (
          <MenuItem key={seconds} onClick={() => { setDurationAnchor(null); void onRetime(scene.index, seconds); }}>
            {seconds}s
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
